import { ISectionLogger } from "@multiweb/logging";
import { FAKE_COMPONENT_VALUE_LOC_KEY } from "@multiweb/reactComponents";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { Enum, EnumType } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { EnumDescription } from "../../../../OW.Remoting/Remoting/DataTypes/IEnums";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FrameContext } from "../UIManagerInterfaces";
import { FakeComponentValueState } from "./ControlEnums";
import { ControlWithLoggerParams, IHasFrameContext, IMultiEdit, ISetValueAfterClickComponent } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export class RadioGroup extends EditableControl implements ISetValueAfterClickComponent, IMultiEdit, IHasFrameContext {
    /**
     * Název css třídy pro nastavení vzhledu hromadné editace pro neznámou hodnotu, která zobrazuje
     * otazníky v jednotlivých položkách.
     */
    private static MULTI_EDIT_UNKNOWN_VALUE = "MultiEditUnknownValue";

    public flow: Flow = Flow.vertical;
    public override innerElement: HTMLDivElement;

    private _clickInitializator: HTMLElement;
    private _controlContainer: HTMLElement;
    private _salt: string;
    private _enumName: string;
    private _hideValues: string;
    private _selectedValue: number;
    private _selectedIndex: number;

    private _comboList: EnumDescription[];
    private _comboListIds: number[] = [];
    private _data: EnumType;
    private readonly _logger: ISectionLogger;
    /** Příznak, zda je aktivní hromadná editace. */
    private _multiEditActive: boolean = false;
    /** Příznak, zda proběhla interakce s komponentou. Slouží pro přepínání vzhledu při hromadné editaci.
     * Po interakci při hromadné editaci se zruší zobrazení otazníků a vrátí se k běžnému zobrazení. */
    private __touched: boolean = false;
    /**
     * Řeší problém při změně výběru. Při setování controlValue je potřeba rozlišit, jestli hodnota null přišla v reakci na stisk backspace nebo to je načtená hodnota z db a podle toho vyrenderovat fake kompnentu.
     */
    private _valueState: FakeComponentValueState;

    private _frameContext: FrameContext | undefined = undefined;

    //#region Properties
    public get frameContext(): FrameContext | undefined {
        return this._frameContext;
    }

    public set frameContext(value: FrameContext | undefined) {
        this._frameContext = value;
    }

    public get enumName(): string {
        return this._enumName;
    }

    public get hideValues(): string {
        return this._hideValues;
    }

    public get selectedValue(): number {
        return this._selectedValue;
    }

    public get touched(): boolean {
        return this._touched;
    }

    public override get controlValue(): Enum {
        if (this._selectedIndex > -1) {
            return this._data.getValue(this._comboList[this._selectedIndex].Name) as Enum;
        }

        return null;
    }

    public override set controlValue(id: Enum) {
        if (id !== undefined && id != null && this._comboListIds.indexOf(id.valueOf()) >= 0) {
            this._selectedValue = id.valueOf();
            this._setCheckedInput(id + "");
        } else {
            this._selectedIndex = -1;
            this._setCheckedInput();
        }
        if (id)
            this._valueState = FakeComponentValueState.userChanged;
        //komponentu je potřeba nastavit jako změněnou pokud uživatel smazal hodnotu přes fake nebo nastavil konkrétní hodnotu
        if (this._valueState !== FakeComponentValueState.notSet && !this._touched)
            this._touched = true;
    }

    public override get displayedValue(): ITextBuilder {
        if (this._multiEditActive) {
            return null;
        }

        if (this._selectedIndex > -1) {
            return this._comboList[this._selectedIndex].Name;
        }

        return null;
    }

    public override set displayedValue(v: ITextBuilder) {
        if (v) {
            const enumDesc = this._comboList.find((e) => e.Name === v.toString());
            if (enumDesc)
                this._setCheckedInput(enumDesc.IntValue.toString());
        }
    }

    public get clickInitializator(): HTMLElement {
        return this._clickInitializator;
    }

    public get multiEditActive(): boolean {
        return this._multiEditActive;
    }

    public set multiEditActive(value: boolean) {
        this._multiEditActive = value;
        this._touched = false;
        this. _valueState = FakeComponentValueState.notSet;
        if (this._multiEditActive) {
            this.activateMultiEditView();
        }
        if (!this._multiEditActive) {
            this.deactivateMultiEditView();
            this._touched = false;
        }
    }

    /**
     * Nastaví příznak, že byla nastavena hodnota v průběhu aktivní hromadné editace (uživatelem nebo programově).
     * Pokud je nastaveno na true v průběhu hromadné editace, zobrazí reálnou hodnotu.
     * @param value Hodnota k nastavení. Hodnota true se nastaví pouze pokud je aktivní hromadná editace.
     */
    private set _touched(value: boolean) {
        this.__touched = this.multiEditActive && value;
        if (this._touched) {
            this.deactivateMultiEditView();
        }
    }

    /**
     * Popis viz {@link __touched}.
     */
    private get _touched(): boolean {
        return this.__touched;
    }

    //#endregion Properties

    constructor({ logger, ...otherParams }: ControlWithLoggerParams) {
        super(otherParams);

        this._logger = logger;
        this.attrName = this.getDataAttr("attrname");
        this.element.classList.add("RadioGroup");
        this.element.classList.remove("EditableControl");
        this._enumName = this.getDataAttr("enumName");
        this._salt = this.element.getAttribute("id");
        this._controlContainer = this.parentElement;
        const flow = this.getDataAttr("flow");
        if (flow)
            this.flow = flow.toLowerCase() == "vertical" ? Flow.vertical : Flow.horizontal;
        if (!this._enumName) {
            this._logger.error("Nebyl zadan povinny atribut enumName");
        }

        this._hideValues = this.getDataAttr("hideValues");

        const captionContainer = this._controlContainer?.querySelector(".CaptionContainer") as HTMLElement;

        if (captionContainer) { // pokud je v CaptionContainer label pro RadioGroup, propojí se kvůli přístupnosti přes aria-labelledby
            this.element.setAttribute("aria-labelledby", captionContainer.querySelector(".Caption")?.id);
        }

        this.element.setAttribute("role", "group"); // nastavení správné role, kvůli přístupnosti

        this._createInput();
        this._setListeners();

        this.isEditingChanged.add((_s: EditableControl, v: any) => {
            const inputs = this.innerElement.querySelectorAll("input");
            for (const el of inputs) {
                if (!v.value)
                    el.setAttribute("disabled", "");
                else
                    el.removeAttribute("disabled");
            }
        });
    }

    /**
     * Načte lokalizační klíč pro získání vysvětlivky pro hromadnou editaci a nastaví ji jako popisek
     * ke všem možnostem.
     */
    private setMultiEditTitle(): void {
        Localization.getDescriptionPromise(FAKE_COMPONENT_VALUE_LOC_KEY).then((description) => {
            if (this.innerElement)
                for (const child of this.innerElement.children) {
                    child.setAttribute("title", description);
                }
        });
    }

    /**
     * Odebere ze všech možností popisek s vysvětlivkou hromadné editace.
     */
    private removeMultiEditTitle(): void {
        for (const child of this.innerElement.children) {
            child.removeAttribute("title");
        }
    }

    /**
     * Aktivuje zobrazení hromadné editace, včetně přidání vysvětlujících popisků.
     */
    private activateMultiEditView(): void {
        this.element.classList.add(RadioGroup.MULTI_EDIT_UNKNOWN_VALUE);
        this.setMultiEditTitle();
    }

    /**
     * Deaktivuje zobrazení hromadné editace, včetně odebrání vysvětlujících popisků.
     */
    private deactivateMultiEditView(): void {
        this.element.classList.remove(RadioGroup.MULTI_EDIT_UNKNOWN_VALUE);
        this.removeMultiEditTitle();
    }

    private _setCheckedInput(inputValue?: string): void {
        const inputs = this.innerElement.querySelectorAll("input");
        for (let i = 0, lng = inputs.length; i < lng; i++) {
            const el: HTMLInputElement = inputs[i] as HTMLInputElement;
            if (el.value == inputValue) {
                el.checked = true;
                this._selectedIndex = i;
                //this.ValueChanged.trigger(this, { selectedIndex: this.selectedIndex });
            } else
                el.checked = false;
        }
    }

    /**
     * Vyhodnotí kliknutí na položku z pohledu hromadné editace. Pokud není hromadná editace aktivní, nic nedělá.
     * Pokud aktivní je, nastaví příznak pro interakci a vypne zobrazení otazníků.
     */
    private _evaluateMultiEditAfterClick(): void {
        this._touched = true;
    }

    private _createInput(): void {
        this.innerElement = document.createElement("div");
        this.innerElement.classList.add("RadioGroupContainer");
        this.innerElement.style.flexDirection = this.flow == Flow.horizontal ? "row" : "column";

        OWManager.getEnum(this._enumName).then((data) => {
            this._data = data;
            this._checkCollection(data);
            let i = 1;
            this._comboList.forEach((el) => {
                const radioItem = document.createElement("div");
                radioItem.classList.add("RadioGroupItem");
                radioItem.style.order = (i++).toString();
                const rad = createElement({
                    type: "input",
                    attrs: {
                        "type": "radio",
                        "name": this.attrName + "_" + this._salt,
                        "id": this.attrName + "_" + this._salt + "_" + i,
                        "value": el.IntValue.toString(),
                    },
                }) as HTMLInputElement;

                if (!this.isEditing)
                    rad.setAttribute("disabled", "");

                const span = document.createElement("label");

                span.setAttribute("for", this.attrName + "_" + this._salt + "_" + i);
                span.addEventListener("click", (e: MouseEvent) => {
                    this._clickInitializator = span;
                    if (!this.enabled || !this.isEditing) {
                        e.preventDefault();
                        return;
                    }
                    const ord = parseInt(radioItem.style.order) - 1;

                    this._selectedIndex = ord;
                    this._evaluateMultiEditAfterClick();
                    this.valueChanged.trigger(this, { Sender: this, selectedIndex: this._selectedIndex });
                    //logger.log(this.data.GetValue(this.comboList[this.selectedIndex - 1].Name), this.selectedIndex, this.comboList);
                });

                if (this._selectedValue && this._selectedValue == el.IntValue)
                    rad.checked = true;

                Localization.addLocTxtData(span, el.LocKey);

                radioItem.appendChild(rad);
                radioItem.appendChild(span);
                this.innerElement.appendChild(radioItem);
            });
        });

        if (this.getDataAttr("lockey"))
            Localization.addLocDescData(this.innerElement, this.getDataAttr("lockey"), "title");

        this.element.appendChild(this.innerElement);
    }

    /** Checkovani hodnot oproti hideValues */
    private _checkCollection(data: EnumType): void {
        // filtrace nechtenych hodnot
        if (this._hideValues) {
            let unwantArr: string[] = this._hideValues.split(/\s*;\s*/); //odstranime pripadne mezery okolo stredniku
            // vymazani pripadnych prazdnych prvku z pole
            unwantArr = unwantArr.filter(Boolean);

            // smaze unwanted keys ze seznamu
            const pom = data.descriptions.filter((el) => {
                return unwantArr.indexOf(el.Name) < 0;
            });
            this._comboList = pom;
        } else {
            this._comboList = data.descriptions;
        }

        this._comboList.forEach((val) => {
            this._comboListIds.push(val.IntValue);
        });
    }

    private _setListeners(): void {
        // Prázdná metoda
    }
}

export enum Flow {
    vertical,
    horizontal,
}
